<template>
    <div :class="[componentClass, searchQuery ? 'search-active' : '']">
        <div class="search-container">
            <CustomInput
                :modelValue="searchQuery"
                @update:modelValue="onSearch"
                :inputKey="searchInputKey"
                :showLabel="false"
                :showDescription="false"
            />
            <button @click.stop="resetSearch" :disabled="searchQuery === ''" class="btn-1 btn-s">
                <div class="icon-reset-neg"></div>
            </button>
        </div>

        <div class="categories-list-container">
            <div class="list-actions">
                <button 
                    class="btn-link" 
                    @click="expandAll"
                    v-if="!isAllExpanded"
                    :disabled="searchQuery !== ''">
                    {{ $t('common.expand_all') }}
                </button>
                <button
                    class="btn-link"
                    @click="collapseAll"
                    v-if="!isAllCollapsed"
                    :disabled="searchQuery !== ''">
                    {{ $t('common.collapse_all') }}
                </button>
            </div>

            <ul class="categories-list">
                <template v-for="item in processedList" :key="item.value">
                    <li 
                        :class="{
                            'has-children': hasChildren(item),
                            'is-child': hasParent(item),
                            'is-expanded': isExpanded(item),
                            'search-direct-match': searchQuery && item.isDirectMatch,
                            'search-parent-match': searchQuery && item.isParentOfMatch && !item.isDirectMatch,
                            ['level-' + item.level]: true,
                            'is-directly-included': isDirectlyIncluded(item),
                            'is-directly-excluded': isDirectlyExcluded(item),
                            'is-effectively-included': !isDirectlyIncluded(item) && isEffectivelyIncluded(item),
                            'is-effectively-excluded': !isDirectlyExcluded(item) && isEffectivelyExcluded(item),
                            'has-selected-descendants': hasIncludedDescendants(item) || hasExcludedDescendants(item),
                        }"
                    >
                        <div
                            class="category-name"
                            @click="toggleExpand(item)"
                            :class="[isExpanded(item) ? 'active' : '']"
                        >
                            <div class="value">{{ formatItemCode(item.value) }}</div>
                            <div class="name">{{ item.text }}</div>
                        </div>
                        <div class="categories-actions">
                            <button 
                                class="btn-include" 
                                :class="[isDirectlyIncluded(item) ? 'active' : isEffectivelyIncluded(item) ? 'inherited' : hasIncludedDescendants(item) ? 'children' : '']" 
                                v-tooltip="showTooltips ? 'tooltips.include' :''"
                                @click="updateItem(item, 'include')">
                                    <div class="icon-check-neg"></div>
                            </button>
                            <button 
                                class="btn-exclude"
                                :class="[isDirectlyExcluded(item) ? 'active' : isEffectivelyExcluded(item) ? 'inherited' : hasExcludedDescendants(item) ? 'children' : '']" 
                                v-tooltip="showTooltips ? 'tooltips.exclude' :''"
                                @click="updateItem(item, 'exclude')">
                                    <div class="icon-close-neg"></div>
                            </button>
                        </div>
                    </li>
                </template>
                <li v-if="!processedList?.length" class="no-results">
                    <div class="h6" @click="resetSearch">{{$t(`fields.search_sbi.no_results`, { query: searchQuery })}}</div>
                </li>
            </ul>
        </div>
    </div>
</template>

<script>
    import { debounce } from 'lodash';
    import CustomInput from '@/components/elements/inputs/CustomInput';

    export default {
        name: 'CustomCategories',
        components: {
            CustomInput
        },
        props: {
            modelValue: {
                type: Object,
                default: () => ({ includes: [], excludes: [] })
            },
            inputKey: {
                type: String,
                required: true
            },
            map: {
                type: Array,
                required: true
            },
            formatCodeFunction: {
                type: Function,
                default: (code) => code
            },
            componentClass: {
                type: String,
                default: 'custom-categories-type-1'
            },
            searchDebounceTime: {
                type: Number,
                default: 600
            },
            showDescription: {
                type: Boolean,
                default: true
            },
            showTooltips: {
                type: Boolean,
                default: true
            },
        },
        data() {
            return {
                searchQuery: '',
                localSelection: {
                    includes: [...(this.modelValue.includes || [])],
                    excludes: [...(this.modelValue.excludes || [])]
                },
                expandedItems: new Set(),
                itemPaths: {},
                itemChildren: {},
                itemParents: {}
            };
        },
        computed: {
            searchInputKey() {
                return `search_${this.inputKey}`;
            },
            categoryMap() {
                return this.map;
            },
            processedList() {
                if (this.searchQuery.trim()) {
                    return this.getFilteredList();
                }
                return this.getProcessedList();
            },
            allExpandableItems() {
                const expandableItems = new Set();
                
                const findExpandableItems = (items) => {
                    if (!items) return;
                    
                    items.forEach(item => {
                        if (item.children && item.children.length > 0) {
                            expandableItems.add(item.value);
                            findExpandableItems(item.children);
                        }
                    });
                };
                
                findExpandableItems(this.categoryMap);
                return expandableItems;
            },
            
            isAllExpanded() {
                if (this.allExpandableItems.size === 0) return true;
                
                for (const value of this.allExpandableItems) {
                    if (!this.expandedItems.has(value)) {
                        return false;
                    }
                }
                return true;
            },
            
            isAllCollapsed() {
                return this.expandedItems.size === 0;
            }
        },
        created() {
            this.buildRelationships(this.categoryMap);
        },
        watch: {
            modelValue: {
                handler(newValue) {
                    this.localSelection = {
                        includes: [...(newValue.includes || [])],
                        excludes: [...(newValue.excludes || [])]
                    };
                },
                deep: true,
                immediate: true
            },
            categoryMap: {
                handler() {
                    this.buildRelationships(this.categoryMap);
                },
                deep: true,
                immediate: true
            }
        },
        methods: {
            formatItemCode(code) {
                return this.formatCodeFunction(code);
            },
            buildRelationships(items) {
                this.itemPaths = {};
                this.itemChildren = {};
                this.itemParents = {};
                
                const buildItemRelationships = (items, parentPath = [], parent = null) => {
                    if (!items) return;
                    
                    items.forEach(item => {
                        const currentPath = [...parentPath, item.value];
                        
                        this.itemPaths[item.value] = currentPath;
                        
                        if (parent) {
                            if (!this.itemParents[item.value]) {
                                this.itemParents[item.value] = [];
                            }
                            this.itemParents[item.value].push(parent.value);
                            
                            if (!this.itemChildren[parent.value]) {
                                this.itemChildren[parent.value] = [];
                            }
                            this.itemChildren[parent.value].push(item.value);
                        }
                        
                        if (item.children && item.children.length > 0) {
                            buildItemRelationships(item.children, currentPath, item);
                        }
                    });
                };
                
                buildItemRelationships(items);
            },
            getAncestors(item) {
                const path = this.itemPaths[item.value] || [];
                return path.slice(0, -1);
            },
            getDescendants(item) {
                const result = [];
                const collect = (value) => {
                    const children = this.itemChildren[value] || [];
                    result.push(...children);
                    children.forEach(child => collect(child));
                };
                collect(item.value);
                return result;
            },
            getSiblings(item) {
                if (!this.itemParents[item.value] || this.itemParents[item.value].length === 0) {
                    return [];
                }
                
                const parentValue = this.itemParents[item.value][0];
                const siblings = [...(this.itemChildren[parentValue] || [])];
                return siblings.filter(value => value !== item.value);
            },
            hasChildren(item) {
                return item.children && item.children.length > 0;
            },
            hasParent(item) {
                return item.level && item.level > 0;
            },
            isDirectlyIncluded(item) {
                return this.localSelection.includes.some(i => i.value === item.value);
            },
            isDirectlyExcluded(item) {
                return this.localSelection.excludes.some(i => i.value === item.value);
            },
            hasIncludedDescendants(item) {
                const descendants = this.getDescendants(item);
                return descendants.some(descendantValue => 
                    this.localSelection.includes.some(i => i.value === descendantValue)
                );
            },
            hasExcludedDescendants(item) {
                const descendants = this.getDescendants(item);
                return descendants.some(descendantValue => 
                    this.localSelection.excludes.some(i => i.value === descendantValue)
                );
            },
            hasAncestorIncluded(item) {
                const ancestors = this.getAncestors(item);
                return ancestors.some(ancestorValue => 
                    this.localSelection.includes.some(i => i.value === ancestorValue)
                );
            },
            hasAncestorExcluded(item) {
                const ancestors = this.getAncestors(item);
                return ancestors.some(ancestorValue => 
                    this.localSelection.excludes.some(i => i.value === ancestorValue)
                );
            },
            isEffectivelyIncluded(item) {
                return this.isDirectlyIncluded(item) || this.hasAncestorIncluded(item);
            },
            isEffectivelyExcluded(item) {
                return this.isDirectlyExcluded(item) || this.hasAncestorExcluded(item);
            },
            findItemByValue(value) {
                let foundItem = null;
                
                const find = (items) => {
                    if (!items || foundItem) return;
                    
                    for (const item of items) {
                        if (item.value === value) {
                            foundItem = item;
                            return;
                        }
                        if (item.children) {
                            find(item.children);
                        }
                    }
                };
                
                find(this.categoryMap);
                return foundItem;
            },
            onSearch: debounce(function(value) {
                this.searchQuery = value;
                
                if (value.trim()) {
                    this.expandMatchingParents();
                }
            }, function() { return this.searchDebounceTime; }),
            resetSearch() {
                this.searchQuery = '';
            },
            isExpanded(item) {
                return this.expandedItems.has(item.value);
            },
            toggleExpand(item) {
                if(this.searchQuery !== '') return;

                if (this.expandedItems.has(item.value)) {
                    this.expandedItems.delete(item.value);
                } else {
                    this.expandedItems.add(item.value);
                }
                this.$forceUpdate();
            },
            expandAll() {
                const expandAllItems = (items) => {
                    if (!items) return;
                    
                    items.forEach(item => {
                        if (item.children && item.children.length > 0) {
                            this.expandedItems.add(item.value);
                            expandAllItems(item.children);
                        }
                    });
                };
                
                expandAllItems(this.categoryMap);
                this.$forceUpdate();
            },
            
            collapseAll() {
                this.expandedItems.clear();
                this.$forceUpdate();
            },
            expandMatchingParents() {
                const query = this.searchQuery.toLowerCase().trim().replace(/\./g, '');
                const matchingPaths = new Set();
                
                const findMatchingPaths = (items, currentPath = []) => {
                    if (!items) return;
                    
                    items.forEach(item => {
                        const newPath = [...currentPath, item.value];
                        const matches = 
                            item.value.toLowerCase().includes(query) || 
                            item.text.toLowerCase().includes(query);
                            
                        if (matches) {
                            for (let i = 0; i < newPath.length - 1; i++) {
                                matchingPaths.add(newPath[i]);
                            }
                        }
                        
                        if (item.children && item.children.length > 0) {
                            findMatchingPaths(item.children, newPath);
                        }
                    });
                };
                
                findMatchingPaths(this.categoryMap);
                
                matchingPaths.forEach(value => {
                    this.expandedItems.add(value);
                });
            },
            getProcessedList() {
                const result = [];
                const processItems = (items, level = 0, parentExpanded = true) => {
                    if (!items) return;
                    
                    items.forEach(item => {
                        const processedItem = { ...item, level };
                        if (parentExpanded) {
                            result.push(processedItem);
                            if (item.children && item.children.length > 0 && this.expandedItems.has(item.value)) {
                                processItems(item.children, level + 1, true);
                            }
                        }
                    });
                };
                
                processItems(this.categoryMap);
                return result;
            },
            getFilteredList() {
                const query = this.searchQuery.toLowerCase().trim().replace(/\./g, '');
                const matchingItemValues = new Set();
                const parentItemValues = new Set();
                
                const findMatches = (items, parentPath = []) => {
                    if (!items) return;
                    
                    items.forEach(item => {
                        const matches = 
                            item.value.toLowerCase().includes(query) || 
                            item.text.toLowerCase().includes(query);
                        
                        if (matches) {
                            matchingItemValues.add(item.value);
                            parentPath.forEach(pValue => parentItemValues.add(pValue));
                        }
                        
                        if (item.children && item.children.length > 0) {
                            findMatches(item.children, [...parentPath, item.value]);
                        }
                    });
                };
                
                findMatches(this.categoryMap);
                
                const result = [];
                
                const processItems = (items, level = 0, parentPath = []) => {
                    if (!items) return;
                    
                    items.forEach(item => {
                        const isDirectMatch = matchingItemValues.has(item.value);
                        const isParentOfMatch = parentItemValues.has(item.value);
                        
                        if (isDirectMatch || isParentOfMatch) {
                            const processedItem = { 
                                ...item, 
                                level,
                                isDirectMatch,
                                isParentOfMatch
                            };
                            result.push(processedItem);
                        }
                        
                        if (item.children && item.children.length > 0 && 
                            (isParentOfMatch || this.expandedItems.has(item.value))) {
                            processItems(item.children, level + 1, [...parentPath, item.value]);
                        }
                    });
                };
                
                processItems(this.categoryMap);
                return result;
            },
            updateParentComponent() {
                const cleanIncludes = this.localSelection.includes.map(item => ({
                    value: item.value,
                    text: item.text || this.getItemTextByValue(item.value)
                }));
                
                const cleanExcludes = this.localSelection.excludes.map(item => ({
                    value: item.value,
                    text: item.text || this.getItemTextByValue(item.value)
                }));
                
                this.$emit('update:modelValue', {
                    includes: cleanIncludes,
                    excludes: cleanExcludes
                });
            },
            getItemTextByValue(value) {
                const item = this.findItemByValue(value);
                return item ? item.text : '';
            },
            getCleanItem(item) {
                return {
                    value: item.value,
                    text: item.text
                };
            },
            updateSelection(includes, excludes) {
                this.localSelection = {
                    includes,
                    excludes
                };
                
                this.updateParentComponent();
            },
            updateItem(item, type = null) {
                if(!type) return;
                let result = null;
                this.updateDescendants(item, type);

                if(type === 'include') {
                    if(this.isDirectlyIncluded(item)) {
                        this.localSelection.includes = this.localSelection.includes.filter(i => i.value !== item.value);
                        result = 'removed';
                    } else if(this.isEffectivelyIncluded(item)) {
                        result = 'pseudo-removed';
                    } else {
                        this.localSelection.includes.push(this.getCleanItem(item));
                        this.localSelection.excludes = this.localSelection.excludes.filter(i => i.value !== item.value);
                        result = 'added';
                    }
                } else if(type === 'exclude') {
                    if(this.isDirectlyExcluded(item)) {
                        this.localSelection.excludes = this.localSelection.excludes.filter(i => i.value !== item.value);
                        result = 'removed';
                    } else if(this.isEffectivelyExcluded(item)) {
                        result = 'pseudo-removed';
                    } else {
                        this.localSelection.excludes.push(this.getCleanItem(item));
                        this.localSelection.includes = this.localSelection.includes.filter(i => i.value !== item.value);
                        result = 'added';
                    }
                }
                
                this.updateParents(item, type, result);
                this.updateParentComponent();
            },
            updateDescendants(item, type = null) {
                if(!type) return;
                const descendants = this.getDescendants(item);
                this.localSelection.includes = this.localSelection.includes.filter(i => !descendants.includes(i.value));
                this.localSelection.excludes = this.localSelection.excludes.filter(i => !descendants.includes(i.value));
            },
            updateParents(item, type = null, result = null) {
                if(!type || (!result && result === null)) return;
                if(!this.itemParents[item.value] || this.itemParents[item.value].length === 0) return;

                const siblings = this.getSiblings(item);
                const parent = this.findItemByValue(this.itemParents[item.value][0]);
                const isParentIncluded = this.isEffectivelyIncluded(parent);
                const isParentExcluded = this.isEffectivelyExcluded(parent);
                const isParentSelected = isParentIncluded || isParentExcluded;

                if(isParentSelected) {
                    if(result === 'pseudo-removed' || result === 'added') {
                        if(isParentIncluded) {
                            siblings.forEach(sibling => {
                                this.localSelection.includes.push(this.getCleanItem(this.findItemByValue(sibling)));
                            });
                        } else if(isParentExcluded) {
                            siblings.forEach(sibling => {
                                this.localSelection.excludes.push(this.getCleanItem(this.findItemByValue(sibling)));
                            });
                        }
                    }

                    this.localSelection.includes = this.localSelection.includes.filter(i => i.value !== this.itemParents[item.value][0]);
                    this.localSelection.excludes = this.localSelection.excludes.filter(i => i.value !== this.itemParents[item.value][0]);

                    const parentType = result !== 'added' ? type : type === 'include' ? 'exclude' : 'include';

                    this.updateParents(this.findItemByValue(this.itemParents[item.value][0]), parentType, result);
                } else {
                    const allSiblingsIncluded = siblings.every(sibling => this.isEffectivelyIncluded(this.findItemByValue(sibling)));
                    const allSiblingsExcluded = siblings.every(sibling => this.isEffectivelyExcluded(this.findItemByValue(sibling)));

                    if(type === 'include' && result === 'added' && allSiblingsIncluded) {
                        this.localSelection.includes.push(this.getCleanItem(this.findItemByValue(this.itemParents[item.value][0])));
                        this.localSelection.excludes = this.localSelection.excludes.filter(i => i.value !== this.itemParents[item.value][0]);
                        this.updateDescendants(this.findItemByValue(this.itemParents[item.value][0]), type);
                        this.updateParents(this.findItemByValue(this.itemParents[item.value][0]), type, result);
                    } else if(type === 'exclude' && result === 'added' && allSiblingsExcluded) {
                        this.localSelection.excludes.push(this.getCleanItem(this.findItemByValue(this.itemParents[item.value][0])));
                        this.localSelection.includes = this.localSelection.includes.filter(i => i.value !== this.itemParents[item.value][0]);
                        this.updateDescendants(this.findItemByValue(this.itemParents[item.value][0]), type);
                        this.updateParents(this.findItemByValue(this.itemParents[item.value][0]), type, result);
                    }
                }                
            }
        }
    }
</script>